import { GeoJsonTypesEnum } from 'src/app/enums/geojson-types.enum';

/** Shapes accepted by the geometry API and upload validation. */
export type Position = [number, number, ...number[]];
export type LinearRing = [
  Position,
  Position,
  Position,
  Position,
  ...Position[]
];
export type PolygonCoordinates = [LinearRing, ...LinearRing[]];

export type PlotGeometry =
  | { type: GeoJsonTypesEnum.POLYGON; coordinates: PolygonCoordinates }
  | {
      type: GeoJsonTypesEnum.MULTIPOLYGON;
      coordinates: [PolygonCoordinates, ...PolygonCoordinates[]];
    };

export type MapGeometry =
  | PlotGeometry
  | { type: GeoJsonTypesEnum.POINT; coordinates: Position };

function isPosition(value: unknown): value is Position {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    Array.from(value).every(
      (coordinate) =>
        typeof coordinate === 'number' && Number.isFinite(coordinate)
    )
  );
}

function isLinearRing(value: unknown): value is LinearRing {
  if (!Array.isArray(value) || value.length < 4) return false;
  const positions = Array.from(value);
  if (!positions.every(isPosition)) return false;
  const first = positions[0];
  const last = positions.at(-1);
  return (
    !!first &&
    !!last &&
    first.length === last.length &&
    first.every((coordinate, index) => coordinate === last[index])
  );
}

function isPolygonCoordinates(value: unknown): value is PolygonCoordinates {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    Array.from(value).every(isLinearRing)
  );
}

export function isMapGeometry(value: unknown): value is MapGeometry {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('type' in value) ||
    !('coordinates' in value)
  )
    return false;
  switch (value.type) {
    case GeoJsonTypesEnum.POINT:
      return isPosition(value.coordinates);
    case GeoJsonTypesEnum.POLYGON:
      return isPolygonCoordinates(value.coordinates);
    case GeoJsonTypesEnum.MULTIPOLYGON:
      return (
        Array.isArray(value.coordinates) &&
        value.coordinates.length > 0 &&
        Array.from(value.coordinates).every(isPolygonCoordinates)
      );
    default:
      return false;
  }
}

export function isPlotGeometry(value: unknown): value is PlotGeometry {
  return isMapGeometry(value) && value.type !== GeoJsonTypesEnum.POINT;
}

export function parseMapGeometry(value: unknown): MapGeometry {
  if (!isMapGeometry(value))
    throw new Error(
      'Expected a nonempty Point, Polygon or MultiPolygon with complete finite coordinates and closed rings.'
    );
  return value;
}

export function parsePlotGeometry(value: unknown): PlotGeometry {
  const geometry = parseMapGeometry(value);
  if (geometry.type === GeoJsonTypesEnum.POINT)
    throw new Error('Expected a Polygon or MultiPolygon for plot review.');
  return geometry;
}
